/**
 * usePushNotifications Hook
 * Manages push notification subscription and permissions
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

/**
 * Convert a base64 string to Uint8Array (for VAPID key)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const isSupported = 'serviceWorker' in navigator && 
                        'PushManager' in window && 
                        'Notification' in window;
    setSupported(isSupported);
    
    if (isSupported) {
      setPermission(Notification.permission);
      loadVapidKey();
      checkExistingSubscription();
    } else {
      setLoading(false);
    }
  }, []);

  // Load VAPID public key from server
  const loadVapidKey = async () => {
    try {
      const response = await api.get('/push/vapid-key');
      if (response.configured) {
        setVapidKey(response.publicKey);
      }
    } catch (err) {
      console.error('[Push] Failed to load VAPID key:', err);
    }
  };

  // Check for existing subscription
  const checkExistingSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSub = await registration.pushManager.getSubscription();
      setSubscription(existingSub);
    } catch (err) {
      console.error('[Push] Error checking subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!supported || !vapidKey) {
      setError('Push notifications not supported or not configured');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }

      // Register service worker if not already
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      }

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to server
      const subJson = pushSubscription.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subJson.endpoint,
        keys: {
          p256dh: subJson.keys.p256dh,
          auth: subJson.keys.auth
        }
      });

      setSubscription(pushSubscription);
      console.log('[Push] Subscription successful');
      return true;

    } catch (err) {
      console.error('[Push] Subscription error:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported, vapidKey]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return true;

    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Remove from server
      await api.delete('/push/subscribe', {
        endpoint: subscription.endpoint
      });

      setSubscription(null);
      console.log('[Push] Unsubscribed successfully');
      return true;

    } catch (err) {
      console.error('[Push] Unsubscribe error:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [subscription]);

  // Send test notification
  const sendTest = useCallback(async () => {
    try {
      await api.post('/push/test');
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, []);

  return {
    // State
    supported,
    permission,
    subscribed: !!subscription,
    loading,
    error,
    
    // Actions
    subscribe,
    unsubscribe,
    sendTest,
    
    // Derived
    canSubscribe: supported && permission !== 'denied' && !subscription && vapidKey,
    canUnsubscribe: !!subscription
  };
}

export default usePushNotifications;
